import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  exportAuditLogsCsv,
  fetchAuditLogDetail,
  fetchAuditLogs,
  fetchAuditSummary,
  unwrapAuditList,
} from "../services/auditService";

const SEVERITY_CLASS = {
  CRITICAL: "history-badge history-badge--danger",
  IMPORTANT: "history-badge history-badge--pending",
  INFO: "history-badge history-badge--neutral",
};

export default function AdminAuditPage() {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    q: "",
    role: "",
    action_type: "",
    entity_type: searchParams.get("entity_type") || "",
    entity_id: searchParams.get("entity_id") || "",
    severity: "",
    status: "",
    date_from: "",
    date_to: "",
    page: 1,
  });
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [exportBusy, setExportBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { ...filters, page_size: 50 };
      const [{ data: list }, { data: sum }] = await Promise.all([
        fetchAuditLogs(params),
        fetchAuditSummary({ date_from: filters.date_from || undefined }),
      ]);
      const unpacked = unwrapAuditList(list);
      setRows(unpacked.rows);
      setCount(unpacked.count);
      setSummary(sum);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load audit logs.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id) => {
    try {
      const { data } = await fetchAuditLogDetail(id);
      setDetail(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load detail.");
    }
  };

  const onExport = async () => {
    setExportBusy(true);
    try {
      const { data } = await exportAuditLogsCsv(filters);
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError("Export failed.");
    } finally {
      setExportBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / 50));

  return (
    <div className="page history-dash">
      <header className="history-dash__header">
        <div>
          <h1 className="history-dash__title">System audit trail</h1>
          <p className="history-dash__meta muted">
            Immutable log of financial and administrative actions. Super Admin critical events are highlighted.
          </p>
        </div>
        <div className="history-dash__header-actions">
          <button type="button" className="history-btn history-btn--secondary" onClick={onExport} disabled={exportBusy}>
            {exportBusy ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </header>

      {summary ? (
        <div className="history-summary-grid">
          <div className="history-summary-card">
            <div className="history-summary-card__label">Total events</div>
            <div className="history-summary-card__value">{summary.total ?? 0}</div>
          </div>
          <div className="history-summary-card">
            <div className="history-summary-card__label">Critical</div>
            <div className="history-summary-card__value">{summary.by_severity?.CRITICAL ?? 0}</div>
          </div>
          <div className="history-summary-card">
            <div className="history-summary-card__label">Important</div>
            <div className="history-summary-card__value">{summary.by_severity?.IMPORTANT ?? 0}</div>
          </div>
        </div>
      ) : null}

      <section className="history-filters-card card">
        <div className="history-filters-grid">
          <input placeholder="Search" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value, page: 1 }))} />
          <select value={filters.severity} onChange={(e) => setFilters((f) => ({ ...f, severity: e.target.value, page: 1 }))}>
            <option value="">All severity</option>
            <option value="CRITICAL">Critical</option>
            <option value="IMPORTANT">Important</option>
            <option value="INFO">Info</option>
          </select>
          <input placeholder="Action type" value={filters.action_type} onChange={(e) => setFilters((f) => ({ ...f, action_type: e.target.value, page: 1 }))} />
          <input placeholder="Entity type" value={filters.entity_type} onChange={(e) => setFilters((f) => ({ ...f, entity_type: e.target.value, page: 1 }))} />
          <input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value, page: 1 }))} />
          <input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value, page: 1 }))} />
        </div>
        <button type="button" className="history-btn history-btn--secondary" onClick={load} disabled={loading}>
          Apply
        </button>
      </section>

      {error ? <p className="msg msg-error">{error}</p> : null}

      <div className="card table-wrap">
        <table className="ops-table">
          <thead>
            <tr>
              <th>When</th>
              <th>User</th>
              <th>Role</th>
              <th>Action</th>
              <th>Entity</th>
              <th>Status</th>
              <th>Severity</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="muted">
                  {loading ? "Loading…" : "No audit entries."}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className={r.severity === "CRITICAL" ? "audit-row--critical" : ""}>
                  <td>{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                  <td>{r.user_display || "—"}</td>
                  <td>{r.role_snapshot || "—"}</td>
                  <td>{r.action_type}</td>
                  <td>
                    {r.entity_type}
                    {r.entity_id ? ` · ${r.entity_id.slice(0, 8)}…` : ""}
                  </td>
                  <td>{r.status}</td>
                  <td>
                    <span className={SEVERITY_CLASS[r.severity] || "history-badge"}>{r.severity}</span>
                  </td>
                  <td>
                    <button type="button" className="btn-secondary compact-btn" onClick={() => openDetail(r.id)}>
                      Detail
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {totalPages > 1 ? (
          <div style={{ padding: 12, display: "flex", gap: 8, alignItems: "center" }}>
            <button type="button" className="btn-secondary compact-btn" disabled={filters.page <= 1} onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}>
              Previous
            </button>
            <span className="muted">
              Page {filters.page} of {totalPages}
            </span>
            <button type="button" className="btn-secondary compact-btn" disabled={filters.page >= totalPages} onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}>
              Next
            </button>
          </div>
        ) : null}
      </div>

      {detail ? (
        <div className="invx-about-modal-overlay" onClick={() => setDetail(null)}>
          <div className="invx-about-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <button type="button" className="invx-about-modal-close" onClick={() => setDetail(null)}>
              ×
            </button>
            <h3>{detail.action_type}</h3>
            <p className="muted">{detail.description || "—"}</p>
            <div className="grid-2" style={{ gap: 12 }}>
              <div>
                <h4>Before</h4>
                <pre style={{ fontSize: 11, overflow: "auto", maxHeight: 240 }}>{JSON.stringify(detail.previous_state, null, 2)}</pre>
              </div>
              <div>
                <h4>After</h4>
                <pre style={{ fontSize: 11, overflow: "auto", maxHeight: 240 }}>{JSON.stringify(detail.new_state, null, 2)}</pre>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <style>{`
        .audit-row--critical td { background: rgba(220, 53, 69, 0.06); }
      `}</style>
    </div>
  );
}
